import type { Anchor } from './timing';

/**
 * Linear drift suggestion (spec §4.1 #2, SP4 drift correction). Pure and
 * framework-free: given two confirmed sync points — where a cue currently is and
 * where it should be — derive the affine correction (rate + offset) and the two
 * {@link Anchor}s the caller feeds to `linearScale` to remove the drift. fps
 * mismatch is the same linear correction and is offered by the frame-rate tool.
 */

/** A confirmed sync point: where a cue is now (`atMs`) vs where it belongs. */
export interface SyncPoint {
  atMs: number;
  shouldBeMs: number;
}

export interface DriftCorrection {
  /** Linear scale factor; 1 = no drift, >1 stretches, <1 compresses. */
  rate: number;
  /** Intercept at t = 0, in ms. */
  offsetMs: number;
  /** The two anchors for `linearScale` that apply this correction. */
  anchors: [Anchor, Anchor];
  /** Drift the correction removes per minute of content, in ms (review metric). */
  driftMsPerMinute: number;
}

/**
 * Derive a linear drift correction from two confirmed sync points. Throws when
 * the two points share a source time (no unique line through them).
 */
export function driftFromSyncPoints(a: SyncPoint, b: SyncPoint): DriftCorrection {
  if (a.atMs === b.atMs) {
    throw new RangeError('driftFromSyncPoints: sync points must have distinct source times.');
  }
  const rate = (b.shouldBeMs - a.shouldBeMs) / (b.atMs - a.atMs);
  const offsetMs = a.shouldBeMs - rate * a.atMs;
  return {
    rate,
    offsetMs,
    anchors: [
      { fromMs: a.atMs, toMs: a.shouldBeMs },
      { fromMs: b.atMs, toMs: b.shouldBeMs },
    ],
    driftMsPerMinute: (rate - 1) * 60_000,
  };
}
